<template>
  <div>
    <v-row>
      <v-col md="3">
        <v-text-field
          hide-details="auto"
          type="date"
          solo
          flat
          background-color="grey lighten-4"
          v-model="searchParams.due_date_start"
          label="Data Inicial"
          @change="select(1)"
        />
      </v-col>
      <v-col md="3">
        <v-text-field
          hide-details="auto"
          type="date"
          solo
          flat
          background-color="grey lighten-4"
          v-model="searchParams.due_date_end"
          label="Data Final"
          @change="select(1)"
        />
      </v-col>
      <v-col md="6">
        <company-select
          chips
          multiple
          v-model="searchParams.company_id"
          :item-value="(val) => val.id"
          :itemText="(val) => val.short_name"
          solo
          flat
          background-color="grey lighten-4"
          :dense="false"
          @change="select(1)"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col md="6">
        <app-text-search-field
          @filter="(showFilter = !showFilter), handleClearFilter()"
          label="Pesquisar por categoria"
          @input="handleSearchInput($event)"
          :loading="isLoading"
        />
      </v-col>

      <v-col md="6">
        <v-chip-group
          v-model="searchParams.status"
          multiple
          mandatory
          @change="select(1)"
        >
          <v-chip filter outlined value="sale"> Vendido </v-chip>
          <v-chip filter outlined value="order"> Pedido </v-chip>
          <v-chip filter outlined value="waiting_approval">
            Aguardando Aprovação
          </v-chip>
          <v-chip filter outlined value="proposal"> Proposta </v-chip>
          <v-chip filter outlined value="draft"> Rascunho </v-chip>
          <v-chip filter outlined value="canceled"> Cancelado </v-chip>
        </v-chip-group>
      </v-col>
    </v-row>

    <v-row v-if="showFilter">
      <v-col md="4">
        <app-select
          :items="deliveryStatusData"
          v-model="searchParams.delivery_type"
          label="Entrega"
          @input="select(1)"
        />
      </v-col>
      <v-col md="4">
        <OperationSelect
          v-model="searchParams.operation_id"
          @input="select(1)"
        />
      </v-col>

      <v-col md="4">
        <SalesmanSearch
          :hidePosition="true"
          clearable
          @input="setSalesman($event)"
        />
      </v-col>
    </v-row>

    <v-row class="mt-6">
      <v-col>
        <v-card flat color="grey lighten-4">
          <v-card-title class="d-flex justify-space-between mb-4 pt-5">
            <span class="text-subtitle-1">
              Relatório de vendas agrupada por categoria</span
            >

            <div class="d-flex gap-4">
              <v-chip-group
                class="mr-8"
                v-model="searchParams.orderBy"
                mandatory
                @change="select(1)"
              >
                <v-chip filter outlined value="category_code"> Código </v-chip>
                <v-chip filter outlined value="category_name">
                  Categoria
                </v-chip>
                <v-chip filter outlined value="quantity"> Quantidade </v-chip>
                <v-chip filter outlined value="total"> Total </v-chip>
              </v-chip-group>

              <v-chip-group
                class="mr-8"
                v-model="searchParams.orderDirection"
                mandatory
                @change="select(1)"
              >
                <v-chip filter outlined value="asc"> A-Z </v-chip>
                <v-chip filter outlined value="desc"> Z-A </v-chip>
              </v-chip-group>

              <v-btn color="primary" class="mr-2"
                >Total:
                {{
                  $format.decimal(
                    sales.data.length > 0 ? sales.data[0].grand_total : 0
                  )
                }}
              </v-btn>

              <v-btn color="success" size="large" @click="handleExport">
                Exportar para Excel
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <v-data-table
              class="grey lighten-4"
              :headers="headers"
              :items="sales.data"
              hide-default-footer
              :items-per-page="-1"
              disable-sort
              @click:row="onRowClick"
            >
              <template v-slot:item.quantity="{ item }">
                {{ $format.decimal(item.quantity) }}
              </template>

              <template v-slot:item.profit_sum="{ item }">
                R${{ $format.decimal(item.profit_sum) }}
              </template>

              <template v-slot:item.percentage="{ item }">
                <span
                  v-if="item.grand_total && parseFloat(item.grand_total) !== 0"
                >
                  {{
                    $format.decimal(
                      (parseFloat(item.total) / parseFloat(item.grand_total)) *
                        100
                    )
                  }}%
                </span>
                <span v-else>
                  0%
                </span>
              </template>

              <template v-slot:item.total="{ item }">
                R${{ $format.decimal(item.total) }}
              </template>
            </v-data-table>

            <app-pagination @click="select($event)" :data="sales" />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import AccountPlanSelect from "@/components/account-plan/ui/AccountPlanSelect.vue";
import CompanySelect from "@/components/company/ui/CompanySelect.vue";
import OperationSelect from "@/components/operation/ui/OperationSelect.vue";
import SalesmanSearch from "@/components/employees/ui/SalesmanSearch.vue";
import SaleDeliveryStatus from "@/components/sales/sections/SaleDeliveryStatus.vue";
import BrandsSearch from "@/components/brands/ui/BrandsSearch.vue";
import CategoriesSearch from "@/components/categories/ui/CategoriesSearch.vue";

import { format, isThisQuarter, startOfMonth } from "date-fns";

export default {
  components: {
    AccountPlanSelect,
    CompanySelect,
    SaleDeliveryStatus,
    SalesmanSearch,
    OperationSelect,
    CategoriesSearch,
    BrandsSearch,
  },

  data() {
    return {
      headers: [
        { text: "Código", value: "category_code", align: "center" },
        { text: "Categoria", value: "category_name", align: "start" },
        { text: "Quantidade", value: "quantity", align: "center" },
        { text: "% Valor total", value: "percentage", align: "end" },
        { text: "Lucro", value: "profit_sum", align: "end" },
        { text: "Total", value: "total", align: "end" },
      ],

      deliveryStatusData: [
        { value: null, text: "Todos" },
        { value: "no_delivery", text: "Sem Entrega" },
        { value: "delivery", text: "Entregue" },
      ],

      sales: {
        data: [],
      },

      showFilter: null,

      searchParams: {
        orderBy: "total",
        orderDirection: "desc",
        company_id: [],
        due_date_start: format(startOfMonth(new Date()), "yyyy-MM-dd"),
        due_date_end: format(new Date(), "yyyy-MM-dd"),
        status: ["sale"],
        salesman_id: null,
        operation_id: null,
        delivery_type: null,
        text: null,
      },

      isLoading: false,
    };
  },

  mounted() {
    this.select(1);
  },

  methods: {
    async select(page = 1) {
      this.searchParams.page = page;
      this.isLoading = true;

      try {
        const response = await this.$http.index(
          "sale/sale-by-category-report",
          this.searchParams
        );
        this.sales = response;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    onRowClick(item) {
      const category_id = item.category_id;
      const category_name = item.category_name;

      this.$router.push({
        path: "/produtos",
        query: { category_id, category_name },
      });
    },

    handleExport() {
      this.$http
        .download("sale/sale-by-category-report-export", this.searchParams)
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `Categorias_${this.searchParams.due_date_start}.xlsx`
          );
          document.body.appendChild(link);
          link.click();
          this.$loading.finish();
        })
        .catch((error) => {
          console.log(error);
          this.$loading.finish();
        });
    },

    setSalesman(salesman) {
      this.searchParams.salesman_id = salesman ? salesman.id : null;
      this.select(1);
    },

    handleRelationshipInput(relationship, field) {
      if (relationship) {
        this.searchParams[field] = relationship.id;
      } else {
        this.searchParams[field] = null;
      }

      this.select(1);
    },

    handleSearchInput(text) {
      this.searchParams.text = text ? text : null;
      this.select(1);
    },

    handleClearFilter() {
      this.searchParams.category_id = null;
      this.searchParams.delivery_type = null;
      this.searchParams.operation_id = null;
      this.searchParams.salesman_id = null;
      this.select(1);
    },
  },
};
</script>
